import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent, type FormEvent } from 'react'
import {
  legacySmsCallingNumbers,
  legacySmsCodes,
  legacySmsOffices,
  legacySmsTemplates,
  type LegacySmsTemplate,
} from '../../mock/legacySms'
import {
  getLegacySmsByteLimit,
  getLegacySmsBytes,
  getLegacySmsMessageType,
  truncateLegacySmsMessage,
  validateLegacySmsReservation,
  type LegacySmsMessageType,
} from './LegacySmsMessageComposer.utils'
import './LegacySmsMessageComposer.css'

export type LegacySmsComposerImage = {
  id: string
  name: string
  previewUrl: string
  source: 'local' | 'template'
}

export type LegacySmsComposerSnapshot = {
  office: string
  message: string
  isAdvertising: boolean
  isReserved: boolean
  reservationDate: string
  reservationTime: string
  callingNumber: string
  images: LegacySmsComposerImage[]
  bytes: number
  type: LegacySmsMessageType
}

type LegacySmsMessageComposerProps = {
  message: string
  recipientCount: number
  onMessageChange: (message: string) => void
  onNotice: (message: string, onConfirm?: () => void) => void
  onRequestConfirm: (message: string, onConfirm: () => void) => void
  onRequestSend: (snapshot: LegacySmsComposerSnapshot) => void
  onOfficeChange?: (officeName: string, officeId: string) => void
}

const MAX_MMS_IMAGES = 3
const FORM_CHANGED_CONFIRM = '변경된 내용을 저장하지 않고 이동하시겠습니까?'
const LEGACY_TIME_OPTIONS = Array.from({ length: 24 * 6 }, (_, index) => {
  const hour = Math.floor(index / 6)
  const minute = (index % 6) * 10
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
})

function formatLegacyDate(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function legacyCalendarDays(year: number, month: number) {
  const first = new Date(year, month, 1)
  const start = new Date(year, month, 1 - first.getDay())
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start)
    date.setDate(start.getDate() + index)
    return date
  })
}

function revokeLocalImages(images: LegacySmsComposerImage[]) {
  images.forEach(image => {
    if (image.source === 'local') URL.revokeObjectURL(image.previewUrl)
  })
}

function templateImages(template: LegacySmsTemplate): LegacySmsComposerImage[] {
  return (template.images ?? []).slice(0, MAX_MMS_IMAGES).map(image => ({ ...image, source: 'template' as const }))
}

export function LegacySmsMessageComposer({ message, recipientCount, onMessageChange, onNotice, onRequestConfirm, onRequestSend, onOfficeChange }: LegacySmsMessageComposerProps) {
  const [office, setOffice] = useState(() => new URLSearchParams(window.location.search).get('office_id') === 'mock-office-b' ? legacySmsOffices[1] : legacySmsOffices[0])
  const [officeMenuOpen, setOfficeMenuOpen] = useState(false)
  const [isAdvertising, setIsAdvertising] = useState(false)
  const [isReserved, setIsReserved] = useState(false)
  const [reservationDate, setReservationDate] = useState('')
  const [reservationTime, setReservationTime] = useState('')
  const [callingNumber, setCallingNumber] = useState(legacySmsCallingNumbers[0])
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [mmsSelected, setMmsSelected] = useState(false)
  const [images, setImages] = useState<LegacySmsComposerImage[]>([])
  const [codeLayerOpen, setCodeLayerOpen] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [timePickerOpen, setTimePickerOpen] = useState(false)
  const [calendarYear, setCalendarYear] = useState(() => new Date().getFullYear())
  const [calendarMonth, setCalendarMonth] = useState(() => new Date().getMonth())
  const [formChanged, setFormChanged] = useState(false)
  const [draggedImageId, setDraggedImageId] = useState<string | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const reservationDateRef = useRef<HTMLInputElement>(null)
  const reservationTimeRef = useRef<HTMLInputElement>(null)
  const reservationPickerRef = useRef<HTMLElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imagesRef = useRef(images)

  imagesRef.current = images

  useEffect(() => () => revokeLocalImages(imagesRef.current), [])

  useEffect(() => {
    const closeReservationPickers = (event: MouseEvent) => {
      if (reservationPickerRef.current?.contains(event.target as Node)) return
      setDatePickerOpen(false)
      setTimePickerOpen(false)
    }
    document.addEventListener('mousedown', closeReservationPickers)
    return () => document.removeEventListener('mousedown', closeReservationPickers)
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    let changed = false

    if (!url.searchParams.has('caller')) {
      url.searchParams.set('caller', 'popup')
      changed = true
    }
    if (!url.searchParams.has('office_id')) {
      url.searchParams.set('office_id', 'mock-office-a')
      changed = true
    }

    if (changed) window.history.replaceState({}, '', `${url.pathname}?${url.searchParams}`)
  }, [])

  const categories = useMemo(() => [...new Set(legacySmsTemplates.map(item => item.category))], [])
  const templateItems = selectedCategory ? legacySmsTemplates.filter(item => item.category === selectedCategory) : []
  const bytes = getLegacySmsBytes(message)
  const type = getLegacySmsMessageType(bytes, mmsSelected)
  const byteLimit = getLegacySmsByteLimit(bytes, type)

  const snapshot = (): LegacySmsComposerSnapshot => ({
    office,
    message,
    isAdvertising,
    isReserved,
    reservationDate,
    reservationTime,
    callingNumber,
    images,
    bytes,
    type,
  })

  const updateMessage = (nextMessage: string, markChanged = true) => {
    const limited = truncateLegacySmsMessage(nextMessage)
    onMessageChange(limited.value)
    if (markChanged) setFormChanged(true)
    if (limited.truncated) onNotice('메시지는 2,000자 이하 입력 가능합니다.')
  }

  const updateOffice = (value: string) => {
    revokeLocalImages(images)
    setOffice(value)
    setOfficeMenuOpen(false)
    setImages([])
    setMmsSelected(false)
    setIsAdvertising(false)
    setIsReserved(false)
    setReservationDate('')
    setReservationTime('')
    setCallingNumber(legacySmsCallingNumbers[0])
    setSelectedCategory(null)
    setSelectedTemplateId(null)
    setCodeLayerOpen(false)
    setDatePickerOpen(false)
    setTimePickerOpen(false)
    onMessageChange('')
    setFormChanged(false)
    const officeId = value === legacySmsOffices[0] ? 'mock-office-a' : 'mock-office-b'
    onOfficeChange?.(value, officeId)
    const url = new URL(window.location.href)
    url.searchParams.set('caller', url.searchParams.get('caller') || 'popup')
    url.searchParams.set('office_id', officeId)
    window.history.replaceState({}, '', `${url.pathname}?${url.searchParams}`)
  }

  const updateAdvertisement = (checked: boolean) => {
    setIsAdvertising(checked)
    updateMessage(checked && !message.startsWith('(광고)') ? `(광고)${message}` : message.replace(/^\(광고\)/, ''))
  }

  const addCode = (code: string) => {
    const node = textareaRef.current
    const start = node?.selectionStart ?? message.length
    const end = node?.selectionEnd ?? message.length
    const nextMessage = `${message.slice(0, start)}${code}${message.slice(end)}`
    updateMessage(nextMessage)
    requestAnimationFrame(() => {
      node?.focus()
      node?.setSelectionRange(start + code.length, start + code.length)
    })
  }

  const createLocalImages = (fileList: FileList | File[]) => {
    const files = Array.from(fileList)
      .filter(file => /image\/(jpeg|gif)/i.test(file.type) || /\.(jpe?g|gif)$/i.test(file.name))
    const baseImages = images.some(image => image.source === 'template') ? [] : images
    const availableCount = MAX_MMS_IMAGES - baseImages.length

    if (availableCount <= 0) {
      onNotice(`이미지는 최대 ${MAX_MMS_IMAGES}장까지 첨부 가능합니다.`)
      return
    }

    if (!files.length) {
      onNotice('JPG 또는 GIF 이미지만 선택할 수 있습니다.')
      return
    }

    const accepted = files.slice(0, availableCount).map((file, index) => ({
      id: `mock-mms-${file.lastModified}-${file.size}-${index}-${Date.now()}`,
      name: file.name,
      previewUrl: URL.createObjectURL(file),
      source: 'local' as const,
    }))

    setImages([...baseImages, ...accepted])
    setMmsSelected(true)
    setFormChanged(true)

    if (files.length > availableCount) onNotice(`이미지는 최대 ${MAX_MMS_IMAGES}장까지 첨부 가능합니다.`)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) createLocalImages(event.target.files)
    event.target.value = ''
  }

  const handleExternalDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.currentTarget.classList.remove('dragover')
    if (!draggedImageId && event.dataTransfer.files.length) createLocalImages(event.dataTransfer.files)
  }

  const reorderImage = (targetId: string) => {
    if (!draggedImageId || draggedImageId === targetId) return
    setImages(current => {
      const fromIndex = current.findIndex(item => item.id === draggedImageId)
      const toIndex = current.findIndex(item => item.id === targetId)
      if (fromIndex < 0 || toIndex < 0) return current
      const next = [...current]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved)
      return next
    })
    setFormChanged(true)
    setDraggedImageId(null)
  }

  const deleteImage = (imageId: string) => {
    if (!window.confirm('선택한 이미지를 삭제하시겠습니까?')) return
    setImages(current => {
      const removed = current.find(image => image.id === imageId)
      if (removed?.source === 'local') URL.revokeObjectURL(removed.previewUrl)
      return current.filter(image => image.id !== imageId)
    })
    setFormChanged(true)
  }

  const applyTemplateContent = (template: LegacySmsTemplate) => {
    revokeLocalImages(images)
    onMessageChange(template.content.replace(/<br\s*\/?>/gi, '\n'))
    setCallingNumber(template.calling)
    setImages(templateImages(template))
    setMmsSelected(template.type === 'MMS' || Boolean(template.images?.length))
    setIsReserved(false)
    setReservationDate('')
    setReservationTime('')
    setSelectedTemplateId(template.id)
    setFormChanged(false)
  }

  const applyTemplate = (template: LegacySmsTemplate) => {
    if (template.id === selectedTemplateId) return
    if (formChanged) {
      onRequestConfirm(FORM_CHANGED_CONFIRM, () => applyTemplateContent(template))
      return
    }
    applyTemplateContent(template)
  }

  const openTemplateSettings = () => {
    const url = new URL('/v1/sms_template/', window.location.origin)
    url.searchParams.set('caller', 'sms')
    url.searchParams.set('office_id', office === legacySmsOffices[0] ? 'mock-office-a' : 'mock-office-b')
    window.open(`${url.pathname}?${url.searchParams}`, '')
  }

  const openDatePicker = () => {
    const parsed = /^\d{4}-\d{2}-\d{2}$/.test(reservationDate) ? new Date(`${reservationDate}T00:00:00`) : new Date()
    if (!Number.isNaN(parsed.getTime())) {
      setCalendarYear(parsed.getFullYear())
      setCalendarMonth(parsed.getMonth())
    }
    setDatePickerOpen(true)
    setTimePickerOpen(false)
  }

  const moveCalendarMonth = (amount: number) => {
    const moved = new Date(calendarYear, calendarMonth + amount, 1)
    setCalendarYear(moved.getFullYear())
    setCalendarMonth(moved.getMonth())
  }

  const selectReservationDate = (date: Date) => {
    setReservationDate(formatLegacyDate(date))
    setCalendarYear(date.getFullYear())
    setCalendarMonth(date.getMonth())
    setDatePickerOpen(false)
    setFormChanged(true)
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()

    if (!message.trim()) {
      onNotice('메시지 내용을 작성해 주세요.')
      textareaRef.current?.focus()
      return
    }

    if (isReserved) {
      if (!reservationDate || !reservationTime) {
        onNotice('예약 일시를 지정해주세요.', () => {
          if (!reservationDate) reservationDateRef.current?.focus()
          else reservationTimeRef.current?.focus()
        })
        return
      }
      const reservationError = validateLegacySmsReservation(reservationDate, reservationTime)
      if (reservationError) {
        onNotice(reservationError)
        return
      }
    }

    if (mmsSelected && images.length === 0) {
      onNotice('이미지 첨부(MMS)가 선택되어 있습니다. 이미지를 첨부해 주세요.')
      return
    }

    onRequestSend(snapshot())
  }

  return <>
    <section className="g_card l_section_msg_content">
      <label className="g_cap_title full">메시지 내용</label>
      <form id="sms-form" noValidate onSubmit={submit}>
        <div id="l_office_section" className="g_input_wrap">
          <label className="g_label title center">지점</label>
          <select id="office_select" className="g_input chosen-select width_2x l_office_select" value={office} onChange={event => updateOffice(event.target.value)} tabIndex={-1} aria-hidden="true">{legacySmsOffices.map(item => <option key={item}>{item}</option>)}</select>
          <div id="office_select_chosen" className={`chosen-container chosen-container-single ${officeMenuOpen ? 'chosen-with-drop chosen-container-active' : ''}`}>
            <a className="chosen-single" role="button" tabIndex={0} aria-expanded={officeMenuOpen} onClick={() => setOfficeMenuOpen(value => !value)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOfficeMenuOpen(value => !value) } }}>
              <span>{office}</span><div><b /></div>
            </a>
            <div className="chosen-drop">
              <div className="chosen-search"><input type="text" readOnly tabIndex={-1} /></div>
              <ul className="chosen-results">{legacySmsOffices.map(item => <li className={office === item ? 'active-result result-selected' : 'active-result'} key={item}><button type="button" onClick={() => updateOffice(item)}>{item}</button></li>)}</ul>
            </div>
          </div>
        </div>
        <section id="l_content_section" className="l_section">
          <div className="g_input_wrap l_content_wrap">
            <div className="l_content_header"><span id="l_form_content_label" className="g_label title center">내용</span><button id="open-code" className="g_btn save size_1" type="button" onClick={() => setCodeLayerOpen(value => !value)}>코드 선택</button></div>
            <div id="l_content_func" className="g_row">
              <label className="g_input checkbox_wrap"><input id="l-ad-btn" className="g_input checkbox l_add_ad" type="checkbox" checked={isAdvertising} onChange={event => updateAdvertisement(event.target.checked)} />광고</label>
              <button className="g_btn size_1" type="button">법규</button>
            </div>
            <textarea ref={textareaRef} className="g_input g_input_textarea l_preview" id="msg-preview" value={message} onChange={event => updateMessage(event.target.value)} required />
            <div id="code-layer" className={`g_card g_card_modal l_code_layer ${codeLayerOpen ? '' : 'l_non_visible'}`}>
              <button id="l_code_close_btn" className="g_btn icon close l_code_close" type="button" onClick={() => setCodeLayerOpen(false)} aria-label="코드 선택 닫기" />
              <span className="g_text comment">* 버튼클릭시 치환코드가 메세지에 삽입됩니다.</span>
              <div id="code-btn-container">{legacySmsCodes.map((item, index) => <button className={`g_btn size_2 l_shoot_schdule_only_btn ${index === 9 || index === 12 ? 'first' : ''}`} type="button" key={item.code} onClick={() => addCode(item.code)}>{item.label}</button>)}</div>
            </div>
          </div>
        </section>
        <section className="l_sms_form_footer">
          <div className="g_input_wrap type_check l_sms_func_wrap">
            <label className={`l_type_label ${type.toLowerCase()}`}>{type}</label><input id="message-type" className="l_msg" type="hidden" value={type} readOnly /><label>{bytes}/{byteLimit}</label><input id="message-count" className="l_msg" type="hidden" value={bytes} readOnly />
            <label id="l_upload_section_label" className="g_input checkbox_wrap"><input type="checkbox" className="g_input checkbox" id="upload-MMS" checked={mmsSelected} onChange={event => { setMmsSelected(event.target.checked); setFormChanged(true) }} />이미지 첨부(MMS)</label>
          </div>
          <section id="upload-section" className={mmsSelected ? '' : 'l_non_appear'}>
            <div
              className="l_mms_thumb_wrap ui-sortable"
              id="thumbnails"
              onDragEnter={event => event.currentTarget.classList.add('dragover')}
              onDragOver={event => event.preventDefault()}
              onDragLeave={event => event.currentTarget.classList.remove('dragover')}
              onDrop={handleExternalDrop}
            >
              {Array.from({ length: MAX_MMS_IMAGES }, (_, index) => {
                const image = images[index]
                return <article
                  className="l_upload_card ui-sortable-handle"
                  key={image?.id ?? `empty-mms-${index}`}
                  draggable={Boolean(image)}
                  onDragStart={() => image && setDraggedImageId(image.id)}
                  onDragEnd={() => setDraggedImageId(null)}
                  onDragOver={event => image && event.preventDefault()}
                  onDrop={event => { event.preventDefault(); event.stopPropagation(); if (image) reorderImage(image.id) }}
                >
                  <div className="g_thumb l_card_cover">
                    <img src={image?.previewUrl ?? ''} alt={image ? `첨부 이미지 ${index + 1}: ${image.name}` : '사용자가 업로드한 썸네일 이미지'} className={`${image ? '' : 'g_image_no_image'} l_cover_src`} />
                  </div>
                  <button className={`g_btn delete size_1 l_btn_mms_thumb_delete ${image ? '' : 'g_hidden'}`} type="button" onClick={() => image && deleteImage(image.id)}>삭제</button>
                </article>
              })}
            </div>
            <span className="g_text comment l_guide">*이미지 업로드는 한 번에 최대 3장까지 다중 선택 가능</span>{' '}
            <span className="g_text comment l_guide">*큰 이미지는 핸드폰으로 작게 변환됨</span>{' '}
            <span className="g_text comment l_guide">*드래그로 이미지 순서 변경</span>
            <div className="g_btn_wrap"><button className="g_btn upload l_btn_img_add" type="button" onClick={() => fileInputRef.current?.click()}>사진 선택</button>{' '}<input id="file-input" type="file" className="g_file g_file_img_mms l_non_appear l_non_visible l_file_img" multiple tabIndex={-1} aria-hidden="true" />{' '}<input ref={fileInputRef} id="file-input-temp" type="file" className="g_file g_file_img_mms g_hidden" accept=".jpg,.jpeg,.gif,image/jpeg,image/gif" multiple onChange={handleFileChange} /></div>
          </section>
        </section>
        <section className="l_more_func_section">
          <div className="g_input_wrap"><label className="g_input checkbox_wrap"><input type="checkbox" className="g_input checkbox" id="reserve-msg" checked={isReserved} onChange={event => { setIsReserved(event.target.checked); if (!event.target.checked) { setDatePickerOpen(false); setTimePickerOpen(false) } setFormChanged(true) }} />예약</label><section ref={reservationPickerRef} className={`l_reserve_section ${isReserved ? '' : 'l_non_visible'}`}>
            <input ref={reservationDateRef} className="l_exp g_input g_input_datepicker hasDatepicker" id="exp-date" type="text" placeholder="YYYY-MM-DD" value={reservationDate} required={isReserved} onFocus={openDatePicker} onClick={openDatePicker} onChange={event => { setReservationDate(event.target.value); setFormChanged(true) }} />{' '}
            <input ref={reservationTimeRef} className="l_exp g_input g_input_timepicker ui-timepicker-input" id="exp-time" type="text" placeholder="HH:MM" value={reservationTime} required={isReserved} onFocus={() => { setTimePickerOpen(true); setDatePickerOpen(false) }} onClick={() => { setTimePickerOpen(true); setDatePickerOpen(false) }} onChange={event => { setReservationTime(event.target.value); setFormChanged(true) }} />
            {datePickerOpen && <div id="ui-datepicker-div" className="ui-datepicker ui-widget ui-widget-content ui-helper-clearfix ui-corner-all">
              <div className="ui-datepicker-header ui-widget-header ui-helper-clearfix ui-corner-all">
                <button className="ui-datepicker-prev ui-corner-all" type="button" title="Prev" onClick={() => moveCalendarMonth(-1)}><span className="ui-icon ui-icon-circle-triangle-w">Prev</span></button>
                <button className="ui-datepicker-next ui-corner-all" type="button" title="Next" onClick={() => moveCalendarMonth(1)}><span className="ui-icon ui-icon-circle-triangle-e">Next</span></button>
                <div className="ui-datepicker-title">
                  <select className="ui-datepicker-year" aria-label="Select year" value={calendarYear} onChange={event => setCalendarYear(Number(event.target.value))}>{Array.from({ length: 111 }, (_, index) => new Date().getFullYear() - 100 + index).map(year => <option key={year} value={year}>{year}</option>)}</select>
                  <select className="ui-datepicker-month" aria-label="Select month" value={calendarMonth} onChange={event => setCalendarMonth(Number(event.target.value))}>{Array.from({ length: 12 }, (_, month) => <option key={month} value={month}>{month + 1}</option>)}</select>
                </div>
              </div>
              <table className="ui-datepicker-calendar"><thead><tr>{['일', '월', '화', '수', '목', '금', '토'].map((day, index) => <th scope="col" className={index === 0 || index === 6 ? 'ui-datepicker-week-end' : ''} key={day}><span title={`${day}요일`}>{day}</span></th>)}</tr></thead><tbody>{Array.from({ length: 6 }, (_, week) => <tr key={week}>{legacyCalendarDays(calendarYear, calendarMonth).slice(week * 7, week * 7 + 7).map((date) => {
                const isOtherMonth = date.getMonth() !== calendarMonth
                const isToday = formatLegacyDate(date) === formatLegacyDate(new Date())
                const isCurrent = formatLegacyDate(date) === reservationDate
                return <td className={`${date.getDay() === 0 || date.getDay() === 6 ? 'ui-datepicker-week-end ' : ''}${isOtherMonth ? 'ui-datepicker-other-month ' : ''}${isToday ? 'ui-datepicker-today ' : ''}${isCurrent ? 'ui-datepicker-current-day' : ''}`} key={formatLegacyDate(date)}><button className={`ui-state-default ${isOtherMonth ? 'ui-priority-secondary' : ''} ${isToday ? 'ui-state-highlight' : ''} ${isCurrent ? 'ui-state-active' : ''}`} type="button" onClick={() => selectReservationDate(date)}>{date.getDate()}</button></td>
              })}</tr>)}</tbody></table>
              <div className="ui-datepicker-buttonpane ui-widget-content"><button className="ui-datepicker-current ui-state-default ui-priority-secondary ui-corner-all" type="button" onClick={() => selectReservationDate(new Date())}>오늘</button><button className="ui-datepicker-close ui-state-default ui-priority-primary ui-corner-all" type="button" onClick={() => { setReservationDate(''); setDatePickerOpen(false); setFormChanged(true) }}>초기화</button></div>
            </div>}
            {timePickerOpen && <div className="ui-timepicker-wrapper" tabIndex={-1}><ul className="ui-timepicker-list">{LEGACY_TIME_OPTIONS.map((time, index) => <li data-time={index * 600} className={index < 72 ? 'ui-timepicker-am' : 'ui-timepicker-pm'} key={time} onMouseDown={event => event.preventDefault()} onClick={() => { setReservationTime(time); setTimePickerOpen(false); setFormChanged(true) }}>{time}</li>)}</ul></div>}
          </section></div>
          <div className="g_input_wrap"><label className="g_label title center">발송번호</label><select className="g_input l_calling_select" value={callingNumber} onChange={event => { setCallingNumber(event.target.value); setFormChanged(true) }}>{legacySmsCallingNumbers.map(item => <option key={item}>{item}</option>)}</select></div>
        </section>
        <section className="l_confirm_section"><div className="g_message_box confirm l_confirm_text l_tag_name row"><span className="l_type_confirm">{type}</span><span className="l_count_confirm">{recipientCount}</span>건 발송</div></section>
        <article className="g_btn_wrap l_btn_container"><button className="g_btn save g_btn_msg_send g_btn_submit" id="save-btn" type="submit">문자발송</button></article>
      </form>
    </section>
    <section className="g_card l_section_template">
      <div className="g_cap_title">메시지 템플릿</div>
      <section className="l_preset_section">
        <div className="l_preset_header"><span className="l_tag_name">템플릿 분류</span><button id="l_setting_btn" className="g_btn size_1" type="button" onClick={openTemplateSettings}>설정</button></div>
        <div className="l_preset_wrapper">
          <article className="g_scroll_bar l_name_container">{categories.map(item => <button className="l_category_btn g_btn" data-selected={selectedCategory === item ? 'true' : undefined} type="button" key={item} onClick={() => setSelectedCategory(item)}>{item}</button>)}</article>
          <section className={`l_title_section ${selectedCategory ? '' : 'l_non_display'}`}>{templateItems.map(item => <button className="l_preset_title g_btn" data-selected={selectedTemplateId === item.id ? 'true' : undefined} type="button" key={item.id} onClick={() => applyTemplate(item)}>{item.name}</button>)}</section>
        </div>
      </section>
    </section>
  </>
}
